/**
 * 轨迹日文显示（门户查单页 /search）
 *
 * 设计：只做「节点翻译」
 *  - 有 status 的轨迹行（实测占 85%）→ 输出日文标准节点文案（术语准确、口径统一）
 *  - 无 status 的自由文本（代理同步、人工填写，约 15%）→ 保留原文，
 *    交浏览器整页翻译（Chrome/Edge「翻訳」）兜底
 *
 * 说明：曾尝试前端直接调公开机器翻译接口兜底长尾，实测存在长句只翻一半、
 * 偶发返回英文、额度限流等问题，且中日共用汉字/标点导致"是否翻干净"无法可靠判定，
 * 会产生"半中半日"效果 —— 故移除。长尾若要日文，需接正规翻译服务（后端 + 缓存）。
 *
 * 变量（日期/预计时间）从原始文案里提取，保证翻译后不丢信息。
 */

/** 该行发生时间 → YYYY-MM-DD */
function fmtDay(sec) {
  if (!sec) return ''
  const n = Number(sec)
  if (!isFinite(n) || n <= 0) return ''
  const d = new Date(n * 1000)
  const p = x => String(x).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

/** 从原始文案里提取某个标签后的日期/时间片段 */
function pick(content, labels) {
  const text = String(content || '')
  for (const label of labels) {
    const re = new RegExp(label + '[：:]?\\s*([0-9]{4}-[0-9]{2}-[0-9]{2}|[0-9]{1,2}/[0-9]{1,2}|[0-9]{1,2}-[0-9]{1,2})')
    const m = re.exec(text)
    if (m && m[1]) return m[1]
  }
  return ''
}

/** 提取「标签（YYYY-MM-DD）」这种括号内日期（如：已到港（2026-10-11）） */
function pickParen(content, labels) {
  const text = String(content || '')
  const re = new RegExp('(?:' + labels + ')[（(]\\s*([0-9]{4}-[0-9]{2}-[0-9]{2})\\s*[）)]')
  const m = re.exec(text)
  return m && m[1] ? m[1] : ''
}

/** 拼接「、ラベル：値」片段，值为空则整段省略 */
function seg(label, value) {
  return value ? `、${label}：${value}` : ''
}

/** 19 个节点 code → 日文标准文案（函数形式，便于带日期变量） */
const STATUS_JA = {
  '1': () => 'ご注文を承りました。まもなく手配いたします',
  '2': () => '集荷を手配しました（Pick up goods）',
  '3': () => '倉庫にて貨物を受領しました（Carry-in）',
  '4': () => '船腹予約（Booking）を手配しました',
  '4.5': () => 'コンテナへの積込みが完了しました（Loaded into container）',
  '5': () => '輸出通関検査中（Customs inspection）',
  '6': () => '輸出通関検査が解除されました',
  '7': it => {
    const kind = pick(it.content, ['开船时间', '起飞时间', '发车时间', '发出时间', '出发时间'])
    const eta = pick(it.content, ['预计到港时间', '预计落地时间', '预计到站时间', '预计到达时间'])
    const head = /航班已起飞/.test(it.content) ? '便は出発しました（Departure）'
      : /已发车/.test(it.content) ? 'トラックが出発しました（Departure）'
        : /已发出/.test(it.content) ? '発送しました（Departure）'
          : /已开船\/起飞\/发车/.test(it.content) ? '出港／出発しました（Departure）'
            : '本船が出港しました（Departure）'
    return head
      + seg(/航班已起飞/.test(it.content) ? '出発日' : /已发车/.test(it.content) ? '出発日' : '出港日',
        kind || fmtDay(it.createTime))
      + seg('到着予定日', eta)
  },
  '8': it => {
    const day = pickParen(it.content, '已到港|已落地|已到站|已到达') || fmtDay(it.createTime)
    const head = /已落地/.test(it.content) ? '到着空港に到着しました'
      : /已到站/.test(it.content) ? '到着駅に到着しました'
        : /已到达/.test(it.content) ? '到着しました'
          : '到着港に到着しました'
    return head + (day ? `（${day}）` : '') + '。以降の手配をお待ちください（arrived to Destination）'
  },
  '9': () => '輸入通関手続中（Customs clearing）',
  '10': () => '輸入通関検査中（Customs inspection）',
  '11': () => '通関許可・リリース済み（Customs clearance）',
  '12': () => 'コンテナを埠頭から引き取り、荷降ろしを手配中です（Container picked up）',
  '13': it => {
    const eta = pick(it.content, ['预计当地时间', '预计入仓时间'])
    return '配送を手配しました。'
      + (eta ? `現地時間 ${eta} 頃の入庫予定です。` : '')
      + 'POD の返送をお待ちください（Delivered）'
  },
  '14': () => '貨物のお受取りが完了しました。POD は注文の添付ファイルでご確認ください',
  '15': it => {
    const etd = pick(it.content, ['预计开船时间'])
    const eta = pick(it.content, ['预计到港时间'])
    return '輸出通関が許可されました' + seg('出港予定日', etd) + seg('到着予定日', eta)
  },
  '16': it => {
    const t = pick(it.content, ['预约派送时间'])
    return '荷降ろしが完了しました' + (t ? `、配送予約：${t}` : '、配送を予約中です') + '。予約情報をご確認ください'
  },
  '17': () => '倉庫から出庫／配送業者へお渡し済み（Has been shipped）'
}

/** 系统固定短语（无 status 或 status 未命中时的中文短语替换） */
const PHRASE_JA = [
  [/定时任务添加物流信息/g, '定期処理により物流情報を追加'],
  [/取消外配订单[:：]?/g, '外部手配をキャンセル：'],
  [/外配订单到/g, '外部手配を次の業者へ：'],
  [/已收到您的订单，尽快处理中/g, 'ご注文を承りました。まもなく手配いたします'],
  [/仓库已收到货物/g, '倉庫にて貨物を受領しました'],
  [/等待进一步操作/g, '以降の手配をお待ちください'],
  [/预计到港时间/g, '到着予定日'],
  [/预计开船时间/g, '出港予定日'],
  [/开船时间/g, '出港日'],
  [/已开船/g, '本船が出港しました'],
  [/已到港/g, '到着港に到着しました'],
  [/已清关/g, '通関許可・リリース済み'],
  [/已提柜/g, 'コンテナを引き取りました'],
  [/派送中/g, '配送中'],
  [/已签收/g, '配達完了']
]

/** 展示语言：URL ?lang=ja 优先，其次浏览器语言为日语时自动 */
export function isJa() {
  try {
    const q = new URLSearchParams(window.location.search)
    const lang = (q.get('lang') || '').toLowerCase()
    if (lang === 'ja' || lang === 'jp') return true
    if (lang === 'zh' || lang === 'cn') return false
  } catch (e) {
    // ignore
  }
  const nav = (navigator.language || navigator.userLanguage || '').toLowerCase()
  return nav.indexOf('ja') === 0
}

/**
 * 轨迹内容 → 展示文本
 * 节点命中 → 日文标准文案；固定短语命中 → 短语日文；其余保留原文（交浏览器翻译）
 * @param {{status?: string, content?: string, createTime?: number}} item
 * @param {boolean} ja 是否日文模式
 */
export function trackText(item, ja) {
  if (!item) return ''
  const raw = item.content == null ? '' : String(item.content)
  if (!ja) return raw
  const fn = STATUS_JA[String(item.status == null ? '' : item.status).trim()]
  if (fn) return fn(item)
  let out = raw
  PHRASE_JA.forEach(([re, to]) => { out = out.replace(re, to) })
  return out
}

export default { isJa, trackText }
